import { Component, Inject, InjectionToken } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DigisubservicesService } from 'src/app/services/digisubservices.service';

export interface SubExpenseItem {
  name: string;
  amount: number;
}

export interface MainExpenseItem {
  name: string;
  amount: number;
  expanded: boolean;
  subExpenses: SubExpenseItem[];
}

export interface BalanceSheetGroup {
  title: string;
  expanded: boolean;
  mainExpenses: MainExpenseItem[];
  total: number;
}

@Component({
  selector: 'app-balance-sheet',
  templateUrl: './balance-sheet.component.html',
  styleUrl: './balance-sheet.component.scss'
})
export class BalanceSheetComponent {

  apiResponse: any[] = []; // this will come from API

  /* ================= HEADER ================= */
  header = {
    name: 'VIVEK PUBLIC TRUST',
    addressLine1: 'Office No 12, CIDCO',
    addressLine2: 'Aurangabad',
    cityState: 'Maharashtra',
    period: 'For the year ended 31-03-2025'
  };

  trust = {
    name: 'VIVEK PUBLIC TRUST',
    registrationNo: 'F/12345/AUR',
    balanceDate: '31-03-2025'
  };

  /* ================= FOOTER ================= */
  footer = {
    place: 'Sambhajinagar',
    udin: '25123456ABCDE',
    date: '31-03-2025',
    caName: 'CA RAMESH JOSHI',
    membershipNo: '123456',
    firmRegNo: 'FRN-009988'
  };

  assets: BalanceSheetGroup[] = [];
  liabilities: BalanceSheetGroup[] = [];
  startDate: any;
  endDate: any;
  isDataReady: boolean = false;
  hasError: boolean = false;
  errorMessage: string = '';
  isAllExpanded: boolean = true;

  constructor(private service: DigisubservicesService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<BalanceSheetComponent>,
    private snack: MatSnackBar,
  ) { }

  ngOnInit(): void {
    this.resetState();

    this.startDate = this.data?.startDate;
    this.endDate = this.data?.endDate;

    if (!this.startDate || !this.endDate) {
      this.hasError = true;
      this.isDataReady = true;
      this.errorMessage = 'Invalid date range';
      return;
    }

    const fyStart = '2024-04-01';

    // =========================
    // CASE 1 : Direct opening
    // =========================
    if (this.startDate === fyStart) {
      const cv = {
        startdate: this.startDate,
        enddate: this.endDate
      };
      this.loadFinalReport(cv);
      return;
    }

    // =========================
    // CASE 2 : 2 Step Flow
    // =========================
    const openingCV = {
      startdate: fyStart,
      enddate: this.getPrevDate(this.startDate)
    };

    this.service.getBalanceSheet(openingCV).subscribe({
      next: (openingResp: any[]) => {
        const openingData = openingResp || [];

        const finalCV = {
          startdate: this.startDate,
          enddate: this.endDate
        };

        this.service.getBalanceSheet(finalCV).subscribe({
          next: (finalResp: any[]) => {
            const txnData = finalResp || [];
            this.apiResponse = this.mergeOpeningWithTransactions(openingData, txnData);
            console.log('API Response for liabilities:', this.apiResponse);
            this.assets = [];
            this.liabilities = [];

            this.buildAllGroups();

            this.hasError = false;
          },
          error: (err) => this.handleError(err)
        });
      },
      error: (err) => this.handleError(err)
    });
  }

  loadFinalReport(cv: any): void {
    this.service.getBalanceSheet(cv).subscribe({
      next: (response: any[]) => {
        this.apiResponse = response || [];
        this.assets = [];
        this.liabilities = [];

        this.buildAllGroups();

        this.hasError = false;
      },
      error: (err) => this.handleError(err)
    });
  }

  getPrevDate(dateStr: string): string {
    const d = new Date(dateStr);
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  }

  mergeOpeningWithTransactions(opening: any[], txn: any[]): any[] {
    const map = new Map<string, any>();

    opening.forEach(r => {
      const key = `${r.exp_name}|${r.sub_exp_name || ''}`;
      map.set(key, { ...r });
    });

    txn.forEach(r => {
      const key = `${r.exp_name}|${r.sub_exp_name || ''}`;
      if (map.has(key)) {
        const obj = map.get(key);
        obj.final_amt = Number(obj.final_amt || 0) + Number(r.final_amt || 0);
        map.set(key, obj);
      } else {
        map.set(key, { ...r });
      }
    });

    return Array.from(map.values());
  }

  handleError(err: any): void {
    this.hasError = true;
    this.isDataReady = true;
    this.errorMessage = err?.error?.error || err?.error?.message || err?.message || 'Failed to load balance sheet data';
    this.snack.open(this.errorMessage, 'Close', {
      duration: 6000,
      verticalPosition: 'top',
      horizontalPosition: 'center',
      panelClass: ['error-snackbar']
    });
  }

  resetState() {
    this.apiResponse = [];
    this.assets = [];
    this.liabilities = [];
    this.isDataReady = false;
    this.hasError = false;
    this.errorMessage = '';
  }

  ngAfterViewInit() {
    window.onbeforeprint = () => this.preparePrint();
    window.onafterprint = () => this.restorePrint();
  }

  originalTableHTML: string | null = null;

  preparePrint() {
    const table = document.querySelector('.main-table') as HTMLElement;
    if (!table) return;
    this.originalTableHTML = table.innerHTML;
  }

  restorePrint() {
    const table = document.querySelector('.main-table') as HTMLElement;
    if (table && this.originalTableHTML) {
      table.innerHTML = this.originalTableHTML;
    }
  }

  toggleExpandCollapse(): void {
    this.isAllExpanded = !this.isAllExpanded;

    // Toggle all groups
    this.assets.forEach(g => g.expanded = this.isAllExpanded);
    this.liabilities.forEach(g => g.expanded = this.isAllExpanded);

    // Toggle all main expenses within groups
    this.assets.forEach(g => {
      g.mainExpenses.forEach(me => me.expanded = this.isAllExpanded);
    });
    this.liabilities.forEach(g => {
      g.mainExpenses.forEach(me => me.expanded = this.isAllExpanded);
    });
  }

  toggleGroup(group: BalanceSheetGroup): void {
    group.expanded = !group.expanded;
  }

  toggleMainExpense(mainExpense: MainExpenseItem, event: Event): void {
    event.stopPropagation(); // Prevent triggering group toggle
    mainExpense.expanded = !mainExpense.expanded;
  }

  getMaxLength(a: any[] = [], b: any[] = []): number {
    return Math.max(a?.length || 0, b?.length || 0);
  }

  /* ================= CALCULATIONS ================= */

  calculateAllTotals(): void {
    // Calculate totals for all groups
    this.assets.forEach(group => {
      group.total = group.mainExpenses.reduce(
        (sum, mainExp) => sum + Number(mainExp.amount || 0),
        0
      );
    });

    this.liabilities.forEach(group => {
      group.total = group.mainExpenses.reduce(
        (sum, mainExp) => sum + Number(mainExp.amount || 0),
        0
      );
    });
  }

  get totalAssets(): number {
    return this.assets.reduce((sum, g) => sum + Number(g.total || 0), 0);
  }

  get totalLiabilities(): number {
    return this.liabilities.reduce((sum, g) => sum + Number(g.total || 0), 0);
  }

  exportExcel(fileName: string): void {
    // Implement export functionality
  }

  printBalanceSheet() {
    const page = document.querySelector('.a4-page') as HTMLElement;
    const table = document.querySelector('.main-table') as HTMLElement;
    const pageHeight = 1122;
    const usedHeight = table.getBoundingClientRect().top;
    if (usedHeight + 200 > pageHeight) {
      table.style.pageBreakBefore = 'always';
    }
    window.print();
  }

  /* ================= BUILD ALL GROUPS ================= */

  async buildAllGroups(): Promise<void> {

    // wait until completed
    await this.buildAdvance();

    // Assets
    this.buildStavarMalmatta();
    this.buildInvestments();
    this.buildCashAndBankAccounts();

    // Liabilities
    this.buildVishwasthVikasNidhi();
    this.buildKarjSurakshitAsurakshit();
    this.buildNafaTotaTaleband();
    this.buildItarDayitvaDeni();
    this.buildRakheevAniItarNidhi();

    this.calculateAllTotals();

    this.isDataReady = true;

  }

  /**
   * Helper method to create a group with proper structure
   */
  // createGroup(title: string, items: any[], mainExpNameField: string = 'exp_name', subExpNameField: string = 'sub_exp_name'): BalanceSheetGroup {
  //   const mainExpensesMap = new Map<string, MainExpenseItem>();

  //   // Group items by main expense name
  //   items.forEach(item => {
  //     const mainName = item[mainExpNameField];
  //     if (!mainName) return;

  //     if (!mainExpensesMap.has(mainName)) {
  //       mainExpensesMap.set(mainName, {
  //         name: mainName,
  //         amount: 0,
  //         expanded: true,
  //         subExpenses: []
  //       });
  //     }

  //     const mainExpense = mainExpensesMap.get(mainName)!;
  //     const amount = Math.abs(Number(item.final_amt || 0));
  //     mainExpense.amount += amount;

  //     // Add sub expense if exists
  //     const subName = item[subExpNameField];
  //     if (subName && subName.trim() !== '') {
  //       mainExpense.subExpenses.push({
  //         name: subName,
  //         amount: amount
  //       });
  //     }
  //   });

  //   return {
  //     title: title,
  //     expanded: true,
  //     mainExpenses: Array.from(mainExpensesMap.values()),
  //     total: Array.from(mainExpensesMap.values()).reduce((sum, me) => sum + me.amount, 0)
  //   };
  // }

  createGroup(
    title: string,
    items: any[],
    mainExpNameField: string = 'exp_name',
    subExpNameField: string = 'sub_exp_name'
  ): BalanceSheetGroup {

    const mainExpensesMap = new Map<string, MainExpenseItem>();

    items.forEach(item => {

      const mainName = item[mainExpNameField];
      if (!mainName) return;

      if (!mainExpensesMap.has(mainName)) {
        mainExpensesMap.set(mainName, {
          name: mainName,
          amount: 0,
          expanded: true,
          subExpenses: []
        });
      }

      const mainExpense = mainExpensesMap.get(mainName)!;

      const rawAmount = Number(item.final_amt || 0); // keep original sign

      // add to total using real value
      mainExpense.amount += rawAmount;

      const subName = item[subExpNameField];

      if (subName && subName.trim() !== '') {
        mainExpense.subExpenses.push({
          name: subName,
          amount: Math.abs(rawAmount) // display only
        });
      }
    });

    // convert totals to positive for display
    const mainExpenses = Array.from(mainExpensesMap.values()).map(me => ({
      ...me,
      amount: Math.abs(me.amount)
    }));

    return {
      title: title,
      expanded: true,
      mainExpenses: mainExpenses,
      total: mainExpenses.reduce((sum, me) => sum + me.amount, 0)
    };
  }

  buildStavarMalmatta(): void {
    const stavarItems = this.apiResponse.filter(r => r.exp_name === 'स्थावर मालमत्ता');

    const group = this.createGroup('स्थावर मालमत्ता – Fixed Assets', stavarItems);

    this.assets = this.assets.filter(a => !a.title.includes('स्थावर मालमत्ता'));
    this.assets.unshift(group);
  }

  buildInvestments(): void {
    const investmentHeads = [
      'गुंतवणूक खाते',
      'मुदत ठेव (विवेक साप्ताहिक)',
      'मुदत ठेव (विश्वस्थ विकास निधी)',
      'बँक ऑफ महाराष्ट्र - रिकरिंग खाते'
    ];

    const investmentItems = this.apiResponse.filter(r => investmentHeads.includes(r.exp_name));

    const group = this.createGroup('गुंतवणूक – Investments', investmentItems);

    this.assets = this.assets.filter(a => !a.title.includes('गुंतवणूक'));
    this.assets.push(group);
  }

  async buildAdvance(): Promise<void> {

    const advanceHeads: string[] = [
      'अंतिम स्टॉक',
      'सुरक्षित अनामत',
      'बँक मुदत ठेवी व्याज येणे बाकी',
      'मुदत ठेवी बँक व्याज येणे बाकी - विवेक',
      'इतर संस्थामधून येणे बाकी',
      'टी डी एस 2023-24',
      'टी डी एस 2024-25'
    ];

    const advanceItems = (this.apiResponse || [])
      .filter((r: any) => advanceHeads.includes(r.exp_name));

    const req1 = {
      startDate: this.startDate,
      endDate: this.endDate,
      issue_seller_name: ''
    };

    const req2 = {
      start_date: this.startDate,
      end_date: this.endDate,
      book_seller_name: ''
    };

    const req3: any = {
      "start_date": this.startDate,
      "end_date": this.endDate,
      "advt_name": ''
    }

    forkJoin({
      issueLedger: this.service.getIssuePersoanlLedBriefly(req1)
        .pipe(catchError(() => of([] as any[]))),

      bookLedger: this.service.getBookSellerLedgerBriefly(req2)
        .pipe(catchError(() => of([] as any[]))),

      advtLedger: this.service.getPersoanlLedBriefly(req3)
        .pipe(catchError(() => of({ data: [] as any[] })))
    })
      .subscribe((result: {
        issueLedger: any[];
        bookLedger: any[];
        advtLedger: { data: any[] };
      }) => {

        const issueLedger = result.issueLedger || [];
        const bookLedger = result.bookLedger || [];
        const advtData = result.advtLedger?.data || [];

        // ✅ अंक विक्री Total
        const ankVikriTotal = issueLedger
          .reduce((sum: number, x: any) =>
            sum + ((+x.bal_amt || 0) * -1), 0);

        // ✅ पुस्तक प्रलंबित देय रक्कम Total
        const bookPendingTotal = bookLedger
          .reduce((sum: number, x: any) =>
            sum + ((+x.bal_amt || 0) * -1), 0);

        // ✅ जाहिरातदार / देणगीदार खाते Total
        const advtOutstandingTotal = advtData
          .reduce((sum: number, x: any) =>
            sum + ((+x.balance_amt || 0) * -1), 0);

        const pushIfNotZero = (name: string, amount: number, type: string) => {
          if (amount !== 0) {
            advanceItems.push({
              exp_name: name,
              sub_exp_name: '',
              final_amt: amount,
              exp_type: type
            });
          }
        };

        pushIfNotZero('अंक विक्री / प्रतिनिधि मुख्य खाते', ankVikriTotal, 'Assent');
        pushIfNotZero('पुस्तक प्रलंबित देय रक्कम', bookPendingTotal, 'Assent');
        pushIfNotZero('जाहिरातदार / देणगीदार खाते', advtOutstandingTotal, 'Assent');

        const group = this.createGroup('आगाऊ – Advance', advanceItems);

        this.assets = this.assets.filter(a => !a.title.includes('आगाऊ'));
        this.assets.push(group);

      });

  }

  buildCashAndBankAccounts(): void {

    const allowedAccounts = [
      'रोख खाते',
      'बँक ऑफ महाराष्ट्र - 20109800154',
      'TJSB सहकारी बँक लि.- 902',
      'ठाणे जनता सह बँक - पुस्तक विभाग',
      'जनता सह बँक लि, - कर्वेरोड शाखा',
      'जनता सह बँक लि - लातूर शाखा',
      'जनता सह बँक लि , औरंगाबाद शाखा',
      'बँक ऑफ महाराष्ट्र - पर्यटन विभाग',
      'TJSB - शिल्पकार चरित्रकोश',
      'TJSB - EVIVEK.COM',
      'TJSB - संवर्धन निधी खाते',
      'बँक ऑफ महाराष्ट्र - संवाद',
      'टपाल अनामत'
    ];

    // 🔹 Filter + Sort by allowedAccounts order
    const cashBankItems = (this.apiResponse || [])
      .filter(r => allowedAccounts.includes(r.exp_name))
      .sort((a, b) =>
        allowedAccounts.indexOf(a.exp_name) -
        allowedAccounts.indexOf(b.exp_name)
      );

    const mainExpenses: MainExpenseItem[] = cashBankItems.map(item => ({
      name: item.exp_name,
      amount: Math.abs(Number(item.final_amt || 0)),
      expanded: true,
      subExpenses: []
    }));

    const group: BalanceSheetGroup = {
      title: 'रोख व बँक खाती व इतर चालू मालमत्ता',
      expanded: true,
      mainExpenses: mainExpenses,
      total: mainExpenses.reduce((sum, me) => sum + me.amount, 0)
    };

    this.assets = this.assets.filter(a => !a.title.includes('रोख व बँक'));
    this.assets.push(group);
  }

  buildVishwasthVikasNidhi(): void {
    const items = this.apiResponse.filter(r => r.exp_name === 'विश्वस्थ विकास निधी');

    const group = this.createGroup('विश्वस्थ विकास निधी', items);

    this.liabilities = this.liabilities.filter(g => !g.title.includes('विश्वस्थ'));
    this.liabilities.push(group);
  }

  buildRakheevAniItarNidhi(): void {

    const items = this.apiResponse.filter(
      r => r.exp_name === 'राखीव व इतर निधी'
    );

    if (!items.length) return;

    const mainExpenses: MainExpenseItem[] = [];
    let total = 0;

    items.forEach(item => {

      const amt = Math.abs(Number(item.final_amt || 0));

      mainExpenses.push({
        name: item.sub_exp_name,
        amount: amt,
        expanded: false,
        subExpenses: []
      });

      total += amt;

    });

    const group: BalanceSheetGroup = {
      title: 'राखीव व इतर निधी',
      expanded: true,
      mainExpenses: mainExpenses,
      total: total
    };

    this.liabilities = this.liabilities.filter(
      g => !g.title.includes('राखीव')
    );

    this.liabilities.push(group);

  }

  buildKarjSurakshitAsurakshit(): void {
    const loanNames = [
      'ठाणे जनता सहकारी बँक - ओडी 111',
      'जनता सहकारी बँक लि, CC - 5039',
      'जनकल्याण सहकारी बँक - OD - 1706',
      'असुरक्षित कर्ज (बिन व्याजी)',
      'असुरक्षित कर्ज (व्याजी)'
    ];

    const items = this.apiResponse.filter(r => loanNames.includes(r.exp_name));

    // For loans, treat each loan type as main expense with no sub-expenses
    const mainExpenses: MainExpenseItem[] = items.map(item => ({
      name: item.exp_name,
      amount: Math.abs(Number(item.final_amt || 0)),
      expanded: true,
      subExpenses: []
    }));

    const group: BalanceSheetGroup = {
      title: 'कर्ज – सुरक्षित / असुरक्षित',
      expanded: true,
      mainExpenses: mainExpenses,
      total: mainExpenses.reduce((sum, me) => sum + me.amount, 0)
    };

    this.liabilities = this.liabilities.filter(g => !g.title.includes('कर्ज'));
    this.liabilities.push(group);
  }

  buildNafaTotaTaleband(): void {
    const profitLossNames = [
      'नफा तोटा - 31/03/2024 ताळेबंद'
    ];

    const items = this.apiResponse.filter(r =>
      profitLossNames.includes(r.exp_name)
    );

    const total = items.reduce((sum, item) =>
      sum + Math.abs(Number(item.final_amt || 0)), 0
    );

    const group: BalanceSheetGroup = {
      title: 'नफा तोटा - 31/03/2024 ताळेबंद',
      expanded: false,        // ← set to false so sub-rows don't expand
      mainExpenses: [{
        name: 'नफा तोटा - 31/03/2024 ताळेबंद',
        amount: total,
        expanded: false,      // ← collapsed, won't show sub-row
        subExpenses: []
      }],
      total: total
    };

    this.liabilities = this.liabilities.filter(
      g => !g.title.includes('नफा तोटा')
    );
    this.liabilities.push(group);
  }

  buildItarDayitvaDeni(): void {

    const otherLiabilitiesNames = [
      'सिद्धी ऑफसेट प्रा लि - अनामत',
      'विलायती राम मित्तल',
      'अभिकर्ता अनामत',
      'वैद्यराज व्हीजन',
      '2024-25 साठी आगाऊ वर्गणी जमा',
      '2024-25 साथी आगाऊ खर्चाची तरतूद',
      'पुरवठादार देणी'
    ];

    const items = this.apiResponse.filter(r =>
      otherLiabilitiesNames.includes(r.exp_name)
    );

    const mainExpensesMap = new Map<string, MainExpenseItem>();

    items.forEach(item => {

      const mainName = item.exp_name;
      const subName = item.sub_exp_name;
      const amount = Number(item.final_amt || 0);

      // 🔴 Special rule for "पुरवठादार देणी"
      if (mainName === 'पुरवठादार देणी') {

        debugger

        if (!mainExpensesMap.has(mainName)) {
          mainExpensesMap.set(mainName, {
            name: mainName,
            amount: 0,
            expanded: false,
            subExpenses: []
          });
        }

        const mainExpense = mainExpensesMap.get(mainName)!;
        mainExpense.amount += amount;

        return; // skip sub-expenses
      }

      // Normal logic for other items
      if (!mainExpensesMap.has(mainName)) {
        mainExpensesMap.set(mainName, {
          name: mainName,
          amount: 0,
          expanded: true,
          subExpenses: []
        });
      }

      const mainExpense = mainExpensesMap.get(mainName)!;

      if (subName && subName.trim() !== '') {
        mainExpense.subExpenses.push({
          name: subName,
          amount: amount
        });
        mainExpense.amount += amount;
      } else {
        mainExpense.amount += amount;
      }

    });

    const group: BalanceSheetGroup = {
      title: 'इतर दायित्व / देणी',
      expanded: true,
      mainExpenses: Array.from(mainExpensesMap.values()),
      total: Array.from(mainExpensesMap.values())
        .reduce((sum, me) => sum + me.amount, 0)
    };

    this.liabilities = this.liabilities.filter(g =>
      !g.title.includes('इतर')
    );

    this.liabilities.push(group);
  }

  closeexp(): void {
    this.dialogRef.close();
  }
}